import React, { useState } from 'react';
import ClaimSourcesEditor from './ClaimSourcesEditor';

const ENTRY_TYPES = [
  { value: 'context', label: 'Context' },
  { value: 'claim', label: 'The claim' },
  { value: 'debunk', label: 'The debunk' },
];

/**
 * Modal form for creating or editing a single claim entry (chronology row).
 * Props:
 *   entry - existing entry data (null for create mode)
 *   topicId - the claim topic this entry belongs to
 *   onSave(entryData) - called with the entry payload
 *   onClose() - close the modal
 */
export default function ClaimEntryForm({ entry = null, topicId, onSave, onClose }) {
  const isEdit = !!entry;
  const [form, setForm] = useState({
    date: entry?.date || '',
    entry_type: entry?.entry_type || 'context',
    summary: entry?.summary || '',
    details: entry?.details || '',
    sources: entry?.sources || [],
    is_draft: entry?.is_draft || false,
    sort_order: entry?.sort_order ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSourcesEditor, setShowSourcesEditor] = useState(false);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.summary.trim()) { setError('Summary is required'); return; }

    setSaving(true);
    setError('');
    try {
      const data = {
        ...form,
        topic_id: topicId,
        is_draft: form.is_draft ? 1 : 0,
      };
      if (isEdit && entry?.id) {
        data.id = entry.id;
      }
      await onSave(data);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>{isEdit ? 'Edit Entry' : 'New Entry'}</h3>
          <button className="admin-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal-body">
            {error && <div className="admin-error">{error}</div>}

            <div className="admin-row">
              <div className="admin-field">
                <label className="admin-label">Type *</label>
                <select className="admin-input" value={form.entry_type} onChange={e => set('entry_type', e.target.value)}>
                  {ENTRY_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="admin-field">
                <label className="admin-label">Date</label>
                <input className="admin-input" value={form.date} onChange={e => set('date', e.target.value)} placeholder="e.g. 7 Oct 2023, or Oct 2023" />
              </div>
              <div className="admin-field">
                <label className="admin-label">Sort order</label>
                <input type="number" className="admin-input" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)} />
              </div>
            </div>

            <label className="admin-label">Summary *</label>
            <textarea
              className="admin-input"
              value={form.summary}
              onChange={e => set('summary', e.target.value)}
              placeholder="Brief summary of this entry..."
              rows={2}
            />

            <label className="admin-label">Details (supports HTML)</label>
            <textarea
              className="admin-input"
              value={form.details}
              onChange={e => set('details', e.target.value)}
              placeholder="Detailed text, can include HTML tags..."
              rows={6}
              style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            />

            {/* Sources */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
              <label className="admin-label" style={{ margin: 0 }}>Sources ({form.sources.length})</label>
              <button type="button" className="admin-btn-add" onClick={() => setShowSourcesEditor(true)}>
                Edit sources
              </button>
            </div>
            {form.sources.length > 0 && (
              <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '4px' }}>
                {form.sources.map((s, i) => (
                  <div key={i}>
                    {i + 1}. {s.sourceName ? s.sourceName.replace(/<[^>]*>/g, '').substring(0, 60) : s.sourceLink?.substring(0, 60) || '(empty)'}
                  </div>
                ))}
              </div>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', fontSize: '0.85rem', color: '#ccc', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.is_draft}
                onChange={e => set('is_draft', e.target.checked)}
              />
              Draft (hidden from public)
            </label>
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (isEdit ? 'Update entry' : 'Create entry')}
            </button>
          </div>
        </form>
      </div>

      {showSourcesEditor && (
        <ClaimSourcesEditor
          sources={form.sources}
          onSave={(sources) => set('sources', sources)}
          onClose={() => setShowSourcesEditor(false)}
        />
      )}
    </div>
  );
}
